import api from './api'

/**
 * Credits API Service
 *
 * Provides methods for managing credit configuration and viewing credit status.
 */
export const creditsService = {
  /**
   * Get platform credit configuration (super admin/platform admin only)
   * @returns {Promise<Object>} Platform credit configuration with ratios
   */
  async getPlatformConfig() {
    try {
      const response = await api.get('/platform/credits/config')
      return response.data
    } catch (error) {
      console.error('Error getting platform credit config:', error)
      throw error
    }
  },

  /**
   * Update platform credit configuration (super admin only)
   * @param {Object} ratios - Credit ratios to update
   * @returns {Promise<Object>} Updated platform credit configuration
   */
  async updatePlatformConfig(ratios) {
    try {
      const response = await api.put('/platform/credits/config', { ratios })
      return response.data
    } catch (error) {
      console.error('Error updating platform credit config:', error)
      throw error
    }
  },

  /**
   * Get tenant credit configuration (super admin/platform admin only)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Tenant credit configuration
   */
  async getTenantCreditConfig(tenantId) {
    try {
      const response = await api.get(`/platform/tenants/${tenantId}/credits`)
      return response.data
    } catch (error) {
      console.error('Error getting tenant credit config:', error)
      throw error
    }
  },

  /**
   * Update tenant credit configuration (super admin/platform admin only)
   * @param {string} tenantId - Tenant ID
   * @param {Object} config - Credit configuration to update
   * @returns {Promise<Object>} Updated tenant credit configuration
   */
  async updateTenantCreditConfig(tenantId, config) {
    try {
      const response = await api.put(`/platform/tenants/${tenantId}/credits`, config)
      return response.data
    } catch (error) {
      console.error('Error updating tenant credit config:', error)
      throw error
    }
  },

  /**
   * Get current tenant's credit status (tenant admin only)
   * @returns {Promise<Object>} Credit status for current tenant
   */
  async getMyCreditStatus() {
    try {
      const response = await api.get('/tenants/credits')
      return response.data
    } catch (error) {
      console.error('Error getting my credit status:', error)
      throw error
    }
  },

  /**
   * List a tenant's credit-grant ledger, newest first (super admin only, #536)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} { grants, bonusCredits, creditsRemaining }
   */
  async listGrants(tenantId) {
    try {
      const response = await api.get(`/platform/tenants/${tenantId}/credit-grants`)
      return response.data
    } catch (error) {
      console.error('Error listing credit grants:', error)
      throw error
    }
  },

  /**
   * Grant credits to a tenant (super admin only, #536)
   * @param {string} tenantId - Tenant ID
   * @param {{amount: number, reason: string, tenantNote?: string}} input - Grant details
   * @returns {Promise<Object>} The created grant
   */
  async createGrant(tenantId, { amount, reason, tenantNote }) {
    try {
      const trimmedNote = tenantNote && tenantNote.trim() ? tenantNote.trim() : undefined
      const payload = { amount, reason, ...(trimmedNote !== undefined && { tenantNote: trimmedNote }) }
      const response = await api.post(`/platform/tenants/${tenantId}/credit-grants`, payload)
      return response.data
    } catch (error) {
      console.error('Error creating credit grant:', error)
      throw error
    }
  },

  /**
   * Reverse a credit grant (super admin only, #536)
   * @param {string} tenantId - Tenant ID
   * @param {string} grantId - Grant ID to reverse
   * @returns {Promise<Object>} { reversal, amountReversed, originalAmount }
   */
  async reverseGrant(tenantId, grantId) {
    try {
      const response = await api.post(`/platform/tenants/${tenantId}/credit-grants/${grantId}/reverse`)
      return response.data
    } catch (error) {
      console.error('Error reversing credit grant:', error)
      throw error
    }
  },

  /**
   * List the current tenant's own credit-grant ledger (tenant admin only, #536)
   * @returns {Promise<Object>} { grants } — redacted projection (date, amount, note)
   */
  async listMyGrants() {
    try {
      const response = await api.get('/tenants/credits/grants')
      return response.data
    } catch (error) {
      console.error('Error listing my credit grants:', error)
      throw error
    }
  }
}

// Human-readable labels for each resource type (user-friendly, no AWS service names)
// Keys must match the backend CreditRatios interface in shared/types.ts
export const CREDIT_RATIO_LABELS = {
  API_CALLS: 'API Calls',
  LAMBDA_INVOCATIONS: 'Processing Operations',
  LAMBDA_DURATION_MS: 'Processing Time (per ms)',
  DYNAMO_RCU: 'Database Read Units',
  DYNAMO_WCU: 'Database Write Units',
  DYNAMO_STORAGE_GB: 'Database Storage (per GB)',
  S3_STORAGE_GB: 'File Storage (per GB)',
  S3_PUT_REQUESTS: 'File Upload Requests',
  S3_GET_REQUESTS: 'File Download Requests',
  S3_EGRESS_GB: 'Download Traffic (per GB)',
  CLOUDFRONT_BYTES_GB: 'Data Transfer (per GB)',
  CLOUDFRONT_REQUESTS: 'Content Delivery Requests',
  COGNITO_USERS: 'Active Users',
  COGNITO_SSO_USERS: 'Active Users (SSO)',
  SES_EMAILS: 'Emails Sent',
  RECAPTCHA_VERIFICATIONS: 'reCAPTCHA Verifications',
  ACTIVE_USER_SEAT: 'Active User Seats',
  SUBMISSION_ARCHIVED: 'Submissions Archived (Cold Storage)'
}

// Resource type icons (emoji) for display
export const CREDIT_RATIO_ICONS = {
  API_CALLS: '🔗',
  LAMBDA_INVOCATIONS: '⚡',
  LAMBDA_DURATION_MS: '⏱️',
  DYNAMO_RCU: '📖',
  DYNAMO_WCU: '✏️',
  DYNAMO_STORAGE_GB: '💾',
  S3_STORAGE_GB: '📦',
  S3_PUT_REQUESTS: '⬆️',
  S3_GET_REQUESTS: '⬇️',
  S3_EGRESS_GB: '📤',
  CLOUDFRONT_BYTES_GB: '🌐',
  CLOUDFRONT_REQUESTS: '📡',
  COGNITO_USERS: '👥',
  COGNITO_SSO_USERS: '🔐',
  SES_EMAILS: '📧',
  RECAPTCHA_VERIFICATIONS: '🛡️',
  ACTIVE_USER_SEAT: '💺',
  SUBMISSION_ARCHIVED: '🗄️'
}

// Resource type categories for grouping
export const CREDIT_RATIO_CATEGORIES = {
  compute: ['API_CALLS', 'LAMBDA_INVOCATIONS', 'LAMBDA_DURATION_MS'],
  database: ['DYNAMO_RCU', 'DYNAMO_WCU', 'DYNAMO_STORAGE_GB'],
  storage: ['S3_STORAGE_GB', 'S3_PUT_REQUESTS', 'S3_GET_REQUESTS', 'SUBMISSION_ARCHIVED'],
  network: ['S3_EGRESS_GB', 'CLOUDFRONT_BYTES_GB', 'CLOUDFRONT_REQUESTS'],
  other: ['COGNITO_USERS', 'COGNITO_SSO_USERS', 'SES_EMAILS', 'RECAPTCHA_VERIFICATIONS', 'ACTIVE_USER_SEAT']
}

export const CREDIT_CATEGORY_LABELS = {
  compute: 'Compute',
  database: 'Database',
  storage: 'Storage',
  network: 'Network',
  other: 'Other Services'
}

// AWS unit costs per resource unit (US East, Jan 2026, no free tier)
// Source: docs/plans/2026-01-24-aws-cost-model.md
// Keys match the backend CreditRatios interface in shared/types.ts
export const AWS_UNIT_COSTS = {
  API_CALLS: 0.0000035,              // $3.50/million
  LAMBDA_INVOCATIONS: 0.0000002,     // $0.20/million
  LAMBDA_DURATION_MS: 0.00000000681, // ARM64 512MB: $0.0000133334/GB-sec * 0.5GB / 1000ms
  DYNAMO_RCU: 0.000000125,           // $0.125/million
  DYNAMO_WCU: 0.000000625,           // $0.625/million
  DYNAMO_STORAGE_GB: 0.25,           // $0.25/GB/month
  S3_STORAGE_GB: 0.023,              // $0.023/GB/month
  S3_PUT_REQUESTS: 0.000005,         // $0.005/1,000
  S3_GET_REQUESTS: 0.0000004,        // $0.0004/1,000
  S3_EGRESS_GB: 0.09,                // $0.09/GB (first 10TB to internet, US-East-1)
  CLOUDFRONT_BYTES_GB: 0.085,        // $0.085/GB
  CLOUDFRONT_REQUESTS: 0.000001,     // $0.01/10,000
  COGNITO_USERS: 0.0055,             // $0.0055/MAU (Lite tier)
  COGNITO_SSO_USERS: 0.015,          // $0.015/MAU (federated SSO)
  SES_EMAILS: 0.0001,                // $0.10/1,000
  RECAPTCHA_VERIFICATIONS: 0.001,    // $0.001/assessment
  // Modeled estimate, not a single AWS unit price: ~2 Glacier IR PUTs ($0.02/1,000)
  // + DynamoDB pointer write + ~1MB at $0.004/GB-month amortized over ~5 years.
  SUBMISSION_ARCHIVED: 0.0003        // per submission archived (#643)
}

/**
 * Calculate the AWS dollar cost per credit for a resource type.
 * @param {string} resourceType - Resource type key (e.g., 'DYNAMO_WCU')
 * @param {number} ratio - Credits per unit (the credit ratio)
 * @returns {number|null} Cost in dollars per credit, or null if not calculable
 */
export function calculateCostPerCredit(resourceType, ratio) {
  const price = AWS_UNIT_COSTS[resourceType]
  if (price === undefined) return null
  if (!ratio || ratio <= 0) return price
  return price / ratio
}

/**
 * Format a cost-per-credit value for display.
 * @param {number|null} cost - Dollar cost per credit
 * @returns {string} Formatted string like "$0.000125" or "—"
 */
export function formatCostPerCredit(cost) {
  if (cost === null || cost === undefined) return '\u2014'
  if (cost === 0) return '$0'

  if (cost < 0.0000001) {
    return `$${cost.toExponential(2)}`
  }

  if (cost < 0.01) {
    // Show enough decimal places to see meaningful digits
    const digits = Math.max(4, -Math.floor(Math.log10(cost)) + 2)
    return `$${cost.toFixed(digits)}`
  }

  return `$${cost.toFixed(4)}`
}

/**
 * Format credits for display
 * @param {number} credits - Number of credits
 * @returns {string} Formatted credits string
 */
export function formatCredits(credits) {
  if (credits === 0) return '0'
  if (credits === null || credits === undefined) return '-'

  // For very small numbers, show as decimal
  if (credits < 1) {
    if (credits < 0.001) {
      return credits.toExponential(2)
    }
    return credits.toFixed(4)
  }

  // Use comma-separated numbers (e.g., 75,000 instead of 75.00K)
  return Math.round(credits).toLocaleString()
}

/**
 * Format a credit ratio for display
 * @param {number} ratio - Credit ratio (credits per unit)
 * @returns {string} Formatted ratio string
 */
export function formatCreditRatio(ratio) {
  if (ratio === 0) return '0'
  if (ratio === null || ratio === undefined) return '-'

  // For very small ratios, use exponential notation
  if (ratio < 0.0001) {
    return ratio.toExponential(2)
  }

  // For small ratios, show more decimal places
  if (ratio < 1) {
    return ratio.toFixed(6)
  }

  // For larger ratios, use standard format
  return ratio.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

export default creditsService
